import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Send, ChevronDown, ChevronUp, Sparkles, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    user_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('company', formData.company);
      data.append('user_type', formData.user_type);
      data.append('message', formData.message);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contact`,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        company: '',
        user_type: '',
        message: ''
      });
      setIsExpanded(false);
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4">
      {/* Collapsed State - Beautiful CTA Card */}
      {!isExpanded && (
        <div 
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 md:p-12 cursor-pointer group transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20"
          onClick={() => setIsExpanded(true)}
          data-testid="contact-expand-btn"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(139,92,246,0.15),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.1),transparent_40%)]"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-3xl"></div>
          
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Left content */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                <span>We respond within 24 hours</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Have Questions?
              </h2>
              <p className="text-gray-400 text-lg max-w-lg">
                Whether you're curious about features, pricing, or want a demo — our team is ready to help.
              </p>
            </div>
            
            {/* Right CTA button */}
            <div className="flex-shrink-0">
              <button 
                className="group/btn relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-105"
              >
                <MessageSquare className="w-5 h-5" />
                <span className="text-lg">Get in Touch</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover/btn:translate-x-1" />
              </button>
            </div>
          </div>
          
          {/* Bottom hint */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 text-gray-500 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
            <span>Click to expand</span>
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </div>
        </div>
      )}

      {/* Expanded State - Contact Form */}
      {isExpanded && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-1 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Gradient border effect */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 opacity-50 blur-sm"></div>
          
          <div className="relative bg-gray-900 rounded-xl p-6 md:p-8">
            {/* Header with close button */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">
                  Let's Connect
                </h2>
                <p className="text-gray-400">
                  Fill out the form below and we'll get back to you shortly.
                </p>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                data-testid="contact-collapse-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300 font-medium">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="John Doe"
                    className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                    data-testid="contact-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300 font-medium">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="john@company.com"
                    className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-gray-300 font-medium">Company</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Company name"
                    className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                    data-testid="contact-company"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user_type" className="text-gray-300 font-medium">I am a... *</Label>
                  <Select
                    value={formData.user_type}
                    onValueChange={(value) => setFormData({ ...formData, user_type: value })}
                    required
                  >
                    <SelectTrigger className="bg-gray-800/50 border-gray-700 text-white focus:border-purple-500 focus:ring-purple-500/20" data-testid="contact-user-type">
                      <SelectValue placeholder="Select your category" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="servicenow_partner" className="text-gray-200 focus:bg-gray-700">ServiceNow Partner</SelectItem>
                      <SelectItem value="end_client" className="text-gray-200 focus:bg-gray-700">End Client</SelectItem>
                      <SelectItem value="consulting_firm" className="text-gray-200 focus:bg-gray-700">Consulting Firm / Recruitment Agency</SelectItem>
                      <SelectItem value="freelance" className="text-gray-200 focus:bg-gray-700">Freelance Consultant</SelectItem>
                      <SelectItem value="servicenow_editor" className="text-gray-200 focus:bg-gray-700">ServiceNow (Editor)</SelectItem>
                      <SelectItem value="other" className="text-gray-200 focus:bg-gray-700">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-300 font-medium">Message *</Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  placeholder="Tell us about your needs or questions..."
                  className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20 resize-none"
                  data-testid="contact-message"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                <Button
                  type="submit"
                  className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/40"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </span>
                  )}
                </Button>
                
                <p className="text-gray-500 text-sm">
                  We typically respond within 24 hours
                </p>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
